import React, { useState, useEffect } from "react";
import { AlertCircle, ArrowLeft, Loader2, Lock, ReceiptText } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Starts checkout. It normally redirects to Snippe's hosted payment page;
   * if it throws, the error is shown and the student can try again.
   */
  onSubmit: () => Promise<void> | void;
  amount?: number;
  description?: string;
  courseName?: string;
}

type PaymentState = "input" | "processing" | "error";

const formatTZS = (amt: number) =>
  new Intl.NumberFormat("en-TZ", {
    style: "currency",
    currency: "TZS",
    minimumFractionDigits: 0,
  }).format(amt);

const fade = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.2 },
};

const PaymentModal: React.FC<PaymentModalProps> = ({
  open,
  onClose,
  onSubmit,
  amount = 150000,
  description = "Programme Fee",
  courseName = "Professional Certification",
}) => {
  const [state, setState] = useState<PaymentState>("input");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (open) {
      setState("input");
      setErrorMsg("");
    }
  }, [open]);

  const handlePay = async () => {
    setState("processing");
    try {
      // On success the browser leaves for Snippe, so the spinner stays up.
      await onSubmit();
    } catch (err: any) {
      setState("error");
      setErrorMsg(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "We couldn't start the payment. Please try again.",
      );
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && state !== "processing") onClose();
      }}
    >
      <DialogContent
        className={cn(
          "max-w-md gap-0 p-0",
          // No close affordance while checkout is opening.
          state === "processing" && "[&>button:last-child]:hidden",
        )}
      >
        {/* Security header */}
        <div className="flex items-center gap-1.5 px-6 pt-5 pr-12 text-success">
          <Lock className="size-3.5" aria-hidden />
          <span className="eyebrow">Secured by Snippe</span>
        </div>

        <div className="px-6 pt-4 pb-6">
          {state === "input" && (
            <motion.div {...fade}>
              <DialogTitle>Payment Method</DialogTitle>
              <DialogDescription className="mt-1 mb-5">
                You'll pay on Snippe's secure checkout with mobile money
              </DialogDescription>

              {/* Order summary */}
              <div className="mb-6 rounded-md border border-dashed border-hairline-strong bg-card p-4">
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="eyebrow text-muted-foreground">Summary</p>
                    <p className="mt-0.5 text-sm font-medium text-foreground">{courseName}</p>
                  </div>
                  <ReceiptText className="size-5 shrink-0 text-muted-foreground" aria-hidden />
                </div>
                <div className="flex items-center justify-between gap-3 border-t border-border pt-3">
                  <span className="text-[13px] text-muted-foreground">Amount to pay</span>
                  <span className="font-mono text-lg font-medium tabular-nums text-foreground">
                    {formatTZS(amount)}
                  </span>
                </div>
              </div>

              <Button size="lg" className="w-full" onClick={handlePay}>
                Continue to payment
              </Button>
            </motion.div>
          )}

          {state === "processing" && (
            <div className="flex flex-col items-center py-6 text-center" aria-live="polite">
              <Loader2 className="mb-5 size-10 animate-spin text-muted-foreground" aria-hidden />
              <DialogTitle>Opening secure checkout</DialogTitle>
              <DialogDescription className="mt-2 px-2">
                You'll be taken to Snippe to pay. Choose your mobile money network there and approve
                the prompt on your phone.
              </DialogDescription>
            </div>
          )}

          {state === "error" && (
            <motion.div {...fade} className="flex flex-col items-center py-2 text-center">
              <span
                className="mb-5 flex size-12 items-center justify-center rounded-full border border-destructive/30 bg-destructive/10 text-destructive"
                aria-hidden
              >
                <AlertCircle className="size-6" />
              </span>
              <DialogTitle>Payment Not Started</DialogTitle>
              <DialogDescription className="mt-2 mb-6" role="alert">
                {errorMsg}
              </DialogDescription>
              <div className="grid w-full grid-cols-2 gap-2">
                <Button variant="outline" onClick={onClose}>
                  <ArrowLeft />
                  Back
                </Button>
                <Button onClick={() => setState("input")}>Try Again</Button>
              </div>
            </motion.div>
          )}
        </div>

        <div className="border-t border-border bg-card px-6 py-4 text-center">
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            Your payment data is encrypted and secure. <br />
            By paying, you agree to our Terms of Service.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;
